import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import useNavigation from '@hooks/useNavigation.tsx';
import { isNotNullOrUndefined, openMailClientCustoms } from '@utils/utils.tsx';
import { PermissionTypesEnum } from '@api/ringil-api.v2';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { RuleInput } from '@components/security/securedDecoration';
import { useCreateCancelSupplierNotificationSidebar } from '@app/pages/customer/supplier-notification/sidebars/CancelSupplierNotificationSidebar';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import MoreButton, { MoreMenuSection } from '@components/RingilComponents/MoreButton/MoreButton.tsx';
import SvgDuplicate from '@components/ringil3/icons/menu/reservation/Duplicate.tsx';
import SvgArchive from '@icons/Archive.tsx';
import SvgMessage from '@icons/Message.tsx';
import SvgXcircle from '@icons/Xcircle.tsx';

interface ComponentProps {
  archived: boolean;
  supplierNotificationId: string;
  supplierNotificationApplicationId: string;
  supplierLocationName: string;
  isEntityOwner: boolean;
  onChange: () => void;
  isDAP: boolean;
  onCanceledSupplierNotification: () => void;
  /** A canceled SN cannot be canceled again. */
  isCanceled?: boolean;
  /**
   * Permission gate for the write actions (duplicate / notify / archive). The detail gates the whole
   * menu at its call site; the list has no such wrapper, so it hands its row-write flag in here.
   * Cancel keeps its own flag either way.
   */
  actionsSecured?: RuleInput;
  buttonSize?: 'small' | 'big';
  /** `table` for a data-table row, the rectangle outline elsewhere. */
  buttonMode?: 'outlined-rectangle' | 'table';
  dataTestId?: string;
}

const SupplierNotificationMoreButtons: React.FC<ComponentProps> = ({
  archived,
  supplierNotificationId,
  supplierNotificationApplicationId,
  supplierLocationName,
  isEntityOwner,
  onChange,
  isDAP,
  onCanceledSupplierNotification,
  isCanceled = false,
  actionsSecured,
  buttonSize = 'big',
  buttonMode,
  dataTestId,
}) => {
  const { tr } = useTranslationLgs();
  const { changeArchiveStateSupplierNotification, getCustomsForSupplierNotification } = useContext(ApiContext);
  const nav = useNavigation();
  const cancelFlag = useSecuredFlag('sn-cancel', {
    kind: 'action',
    description: 'Cancel a supplier notification',
    permissions: [PermissionTypesEnum.PERMISSION_SN_CANCEL],
  });
  const [visibleConfirmDialog, setVisibleConfirmDialog] = useState(false);
  const { CancelSupplierNotificationSidebar, setCancelationRequest, cancelationRequest } = useCreateCancelSupplierNotificationSidebar();

  const archive = () => {
    changeArchiveStateSupplierNotification(supplierNotificationId, { archive: true }, () => {
      setVisibleConfirmDialog(false);
      onChange();
    });
  };

  const sections: MoreMenuSection[] = [
    {
      items: [
        {
          id: 'duplicate',
          secured: actionsSecured,
          label: tr('MoreButtonsSupplierNotification.duplicate', 'Duplicate'),
          icon: <SvgDuplicate />,
          command: () => nav.navigate(nav.urlFunctions.createDuplicateSupplierNotification(supplierNotificationId)),
          visible: !isDAP,
          dataTestId: 'sn-moreButton-duplicate',
        },
        {
          id: 'notify-warehouse',
          secured: actionsSecured,
          label: tr('MoreButtonsSupplierNotification.notifyWarehouse', 'Notify warehouse'),
          icon: <SvgMessage />,
          command: () => openMailClientCustoms(getCustomsForSupplierNotification, supplierNotificationId, supplierLocationName, supplierNotificationApplicationId),
          visible: !archived,
          dataTestId: 'sn-moreButton-notifyWarehouse',
        },
        {
          id: 'archive',
          secured: actionsSecured,
          label: tr('MoreButtonsSupplierNotification.archive', 'Archive'),
          icon: <SvgArchive />,
          command: () => setVisibleConfirmDialog(true),
          visible: !archived && isEntityOwner,
          dataTestId: 'sn-moreButton-archive',
        },
        {
          id: 'cancel',
          label: tr('SupplierNotificationMoreButton.cancel', 'Cancel supplier notification'),
          icon: <SvgXcircle />,
          command: () => setCancelationRequest({ supplierNotificationId }),
          visible: !isCanceled,
          secured: cancelFlag,
          dataTestId: 'sn-moreButton-cancelNotification',
        },
      ],
    },
  ];

  return (
    <>
      <MoreButton sections={sections} buttonSize={buttonSize} buttonMode={buttonMode} dataTestId={dataTestId ?? 'sn-moreButton'} />

      <ConfirmDialog
        visible={visibleConfirmDialog}
        dialogTitle={tr(`MoreButtonsSupplierNotification.archiveEntity`, `Do you want to archive this record?`)}
        dialogMessage={tr(`MoreButtonsSupplierNotification.switchToManualModeDialogBody`, `Do you want to archive this record?`)}
        onCancel={() => setVisibleConfirmDialog(false)}
        onConfirm={() => archive()}
        onAcceptButtonText={tr(`MoreButtonsSupplierNotification.yesArchive`, `Archive`)}
        onCancelButtonText={tr(`MoreButtonsSupplierNotification.close`, `Close`)}
      />

      {isNotNullOrUndefined(cancelationRequest) && <CancelSupplierNotificationSidebar onContinue={() => onCanceledSupplierNotification()} />}
    </>
  );
};

export default SupplierNotificationMoreButtons;
